import { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import useMartyContext from 'hooks/useMartyContext';

import css from 'styles/components/common/doubleSlider.scss';

// eslint-disable-next-line prefer-arrow-callback
const DoubleSlider = forwardRef(function DoubleSlider(props, ref) {
  const {
    minLabel, maxLabel,
    minLimit, maxLimit,
    minDefault, maxDefault,
    onChange, step
  } = props;

  const { testId } = useMartyContext();

  const [min, setMin] = useState(minDefault || minLimit);
  const [max, setMax] = useState(maxDefault || maxLimit);
  const [minTouched, setMinTouched] = useState(false);
  const [maxTouched, setMaxTouched] = useState(false);

  // Allow for manually resetting the slider via a ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      setMin(minDefault || minLimit);
      setMax(maxDefault || maxLimit);
      setMinTouched(false);
      setMaxTouched(false);
    }
  }));

  const onMinChange = e => {
    const value = e.target.valueAsNumber;
    // Ensure we are at least 1 `step` distance away from the max
    if (max - step >= value) {
      setMin(value);
      onChange?.({ min: value, max });
      setMinTouched(true);
    } else if (value >= max) {
      // Move other slider if we're up against it
      const newMaxValue = value + step;
      // Only do this if we're not already at the edge of the slider
      if (newMaxValue <= maxLimit) {
        setMin(value);
        setMax(newMaxValue);
        onChange?.({ min: value, max: newMaxValue });
        setMinTouched(true);
      }
    }
  };

  const onMaxChange = e => {
    const value = e.target.valueAsNumber;
    // // Ensure we are at least 1 `step` distance away from the min
    if (value - min >= step) {
      setMax(value);
      onChange?.({ min, max: value });
      setMaxTouched(true);
    } else if (value <= min) {
      // Move other slider if we're up against it
      const newMinValue = value - step;
      // Only do this if we're not already at the edge of the slider
      if (newMinValue >= minLimit) {
        setMin(newMinValue);
        setMax(value);
        onChange?.({ min: newMinValue, max: value });
        setMaxTouched(true);
      }
    }
  };

  return (
    <div className={css.container}>
      <label htmlFor={minLabel}>{minLabel}</label>
      <input
        className={cn({ [css.touched]: minTouched })}
        data-test-id={testId('minSlider')}
        min={minLimit}
        max={maxLimit}
        id={minLabel}
        type="range"
        step={step}
        value={min}
        onChange={onMinChange} />
      <label htmlFor={maxLabel}>{maxLabel}</label>
      <input
        className={cn({ [css.touched]: maxTouched })}
        data-test-id={testId('maxSlider')}
        min={minLimit}
        max={maxLimit}
        id={maxLabel}
        type="range"
        step={step}
        value={max}
        onChange={onMaxChange} />
    </div>
  );
});

DoubleSlider.propTypes = {
  minLabel: PropTypes.string.isRequired,
  maxLabel: PropTypes.string.isRequired,
  minLimit: PropTypes.number,
  maxLimit: PropTypes.number.isRequired,
  minDefault: PropTypes.number,
  maxDefault: PropTypes.number,
  onChange: PropTypes.func,
  step: PropTypes.number
};

DoubleSlider.defaultProps = {
  minLimit: 0,
  step: 20
};

export default DoubleSlider;
